import React from 'react';
import { OrganizerVerification, PRICE_AUDIENCE_LABELS, premiumState } from '@radevents/shared';
import { PremiumCta } from './manage/chrome';
import { BERRY, BORDER, FONT_MONO, INK, LABEL, MUTED } from './organizerSpace';

/** Rust statt des hellen [AMBER]: das hier ist kleiner Text auf hellem Grund (~6:1 statt ~2:1). */
const WARN = '#8C3B0B';
const WARN_BG = 'rgba(140,59,11,.09)';

const day = (iso: string) => new Date(iso).toLocaleDateString('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
});

const inDays = (n: number) => (n <= 0 ? 'heute' : n === 1 ? 'morgen' : `in ${n} Tagen`);

interface Props {
  verification?: OrganizerVerification;
  /**
   * `bar` — eine Zeile im Pflege-Balken über der Seite, immer im Blick.
   * `card` — die ausführliche Auskunft im Reiter *Sichtbarkeit*, wo die Buchungen stehen.
   */
  variant: 'bar' | 'card';
  /**
   * Es liegt eine Anfrage bei uns, noch nicht freigeschaltet.
   *
   * Eigener Zustand, weil „nichts gebucht" und „gewartet auf uns" für den Veranstalter zwei
   * verschiedene Nachrichten sind: im zweiten Fall wäre ein Knopf „Premium freischalten" die
   * Aufforderung, ein zweites Mal zu bestellen.
   */
  requested?: boolean;
  /** Öffnet die Buchungsanfrage bzw. den Premium-Dialog. Ohne den Weg wäre die Warnung eine Sackgasse. */
  onExtend?: () => void;
}

const CSS = `
.om-premium { font-size:12.5px; font-weight:700; white-space:nowrap; border-radius:999px;
  padding:4px 10px; border:1px solid transparent; background:rgba(255,255,255,.72); color:${LABEL}; }
/* Im Pflege-Balken rechts. Beim Umbruch faellt die Pille nach links zurueck. */
.om-hint > .om-premium { margin-left:auto; }
@media (max-width:520px) { .om-hint > .om-premium { margin-left:0; } }
.om-premium--warn { color:${WARN}; background:${WARN_BG}; border-color:rgba(140,59,11,.22);
  font-family:inherit; cursor:pointer; }
.om-premium--warn:hover { background:rgba(140,59,11,.15); }
.om-premium--warn:focus-visible { outline:2px solid ${WARN}; outline-offset:2px; }
`;

/**
 * Zeigt, ob Premium läuft und wie lange noch.
 *
 * Ohne diese Zeile bemerkt der Ablauf niemand: es gibt keinen Zahlungsanbieter und damit keine
 * automatische Verlängerung. Von einem Tag auf den anderen wären die Zahlen im Reiter weg, der
 * Banner von der Seite verschwunden und die Erinnerungen an die Teilnehmer eingestellt — ohne
 * dass es jemand angekündigt hätte. (Der Haken bleibt: er hängt nicht an der Buchung.)
 */
const PremiumStatus: React.FC<Props> = ({ verification, variant, requested, onExtend }) => {
  const state = premiumState(verification);

  // Nichts gebucht (oder längst vorbei). In der Leiste sagt das die Pille „Basis-Zugang" —
  // hier steht dann nichts. Im Reiter *Sichtbarkeit* dagegen ist genau das die Stelle, an der
  // erklärt wird, was fehlt: ein leerer Kasten wäre dort die verschenkte Gelegenheit.
  if (!state || !verification) {
    if (variant === 'bar') return null;
    return (
      <div className="vb-lockcard">
        <div className="vb-lockcard-main">
          <div style={{
            fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '.1em',
            textTransform: 'uppercase', color: BERRY,
          }}>
            {requested ? 'Zahlung ausstehend' : 'Premium'}
          </div>
          <h3 className="vb-title" style={{ marginTop: 6, color: INK }}>
            {requested ? 'Premium ist bestellt' : 'Noch nicht freigeschaltet'}
          </h3>
          <p>
            {requested
              ? 'Sobald deine Zahlung eingegangen ist, schalten wir Premium frei. Die Zahlungsdaten findest du unter Bestellungen & Änderungen.'
              : 'Automatische Erinnerungen für Interessierte, ein eigenes Titelbild und Statistiken zu deinen Events.'}
          </p>
        </div>
        {!requested && onExtend && (
          <button className="vb-btn vb-btn--primary" onClick={onExtend}>Jetzt freischalten</button>
        )}
      </div>
    );
  }

  const until = day(verification.until);
  const from = day(verification.from);

  if (variant === 'bar') {
    // Liegt die Verlaengerung schon bei uns, faellt die Aufforderung weg: die Pille sagt dann,
    // was gilt, und nicht, was zu tun waere. Ein Knopf, der in eine abgewiesene zweite Bestellung
    // fuehrt, ist schlimmer als keiner.
    const urgent = (state.kind === 'expiring' || state.kind === 'expired') && !requested;
    const text = state.kind === 'active' ? `Premium bis ${until}`
      : state.kind === 'planned' ? `Premium ab ${from}`
      : requested
        ? `${state.kind === 'expired' ? 'Premium abgelaufen' : `Premium endet ${inDays(state.days)}`} · Verlängerung bestellt, Zahlung ausstehend`
        : state.kind === 'expiring' ? `Premium endet ${inDays(state.days)} · Verlängern`
          : 'Premium abgelaufen · Erneut buchen';
    return (
      <>
        <style>{CSS}</style>
        {urgent && onExtend ? (
          <button type="button" className="om-premium om-premium--warn" onClick={onExtend}
            title={state.kind === 'expiring'
              ? `Läuft bis ${until}. Danach sind dein Titelbild, die Statistiken und die automatischen Erinnerungen wieder gesperrt.`
              : `Lief bis ${until}.`}>
            {text}
          </button>
        ) : (
          <span className="om-premium" title={`Gebucht ab ${from}`}>{text}</span>
        )}
      </>
    );
  }

  const title = state.kind === 'active' || state.kind === 'expiring'
    ? `Premium läuft bis ${until}`
    : state.kind === 'planned' ? `Premium beginnt am ${from}`
    : `Premium ist am ${until} abgelaufen`;

  const meta = state.kind === 'active' || state.kind === 'expiring'
    ? `Noch ${state.days} ${state.days === 1 ? 'Tag' : 'Tage'} · für ${PRICE_AUDIENCE_LABELS[verification.tier]} · seit ${from}`
    : state.kind === 'planned'
      ? `Beginnt ${inDays(state.days)} und läuft dann bis ${until}.`
      : `Dein Titelbild, die Statistiken und die automatischen Erinnerungen sind seit ${until} wieder gesperrt.`;

  return (
    <div
      className="vb-card"
      style={{
        marginBottom: 22,
        borderColor: state.kind === 'expiring' || state.kind === 'expired' ? 'rgba(140,59,11,.35)' : BORDER,
        background: state.kind === 'expiring' || state.kind === 'expired' ? WARN_BG : undefined,
      }}
    >
      <div className="vb-card-main">
        <div style={{
          fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase',
          color: state.kind === 'expiring' || state.kind === 'expired' ? WARN : LABEL,
        }}>
          {state.kind === 'expiring' ? 'Premium läuft bald ab'
            : state.kind === 'expired' ? 'Premium abgelaufen' : 'Premium'}
        </div>
        <h3 className="vb-title" style={{ marginTop: 6, color: INK }}>{title}</h3>
        <p className="vb-meta" style={{ color: state.kind === 'expired' ? WARN : MUTED }}>{meta}</p>
      </div>
      {onExtend && state.kind !== 'planned' && (
        <div className="vb-actions">
          <PremiumCta requested={requested} onClick={onExtend} className="vb-btn vb-btn--berry">
            {state.kind === 'expired' ? 'Erneut buchen' : 'Verlängern'}
          </PremiumCta>
        </div>
      )}
    </div>
  );
};

export default PremiumStatus;
